import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { api } from '../../lib/api.js';
import {
  PLACEHOLDER_EDITIONS,
  splitEditionColumns,
} from '../../lib/welcomeEditions.js';
import WelcomeEditionCard from './WelcomeEditionCard.jsx';

/** Pixels moved per second — both columns use this for matched visual speed. */
const SCROLL_PX_PER_SEC = 32;

/** Triple rows so -33.33% loop reset is seamless with flex gap. */
function buildLoop(rows) {
  if (rows.length === 0) return [];
  return [...rows, ...rows, ...rows];
}

function useScrollDuration(trackRef, rows) {
  const [durationSec, setDurationSec] = useState(55);

  useLayoutEffect(() => {
    const el = trackRef.current;
    if (!el) return;

    const measure = () => {
      const loopHeight = el.scrollHeight / 3;
      if (loopHeight > 0) {
        setDurationSec(Math.max(loopHeight / SCROLL_PX_PER_SEC, 24));
      }
    };

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [rows]);

  return durationSec;
}

function WelcomeColumn({ rows, offset = false }) {
  const trackRef = useRef(null);
  const loop = buildLoop(rows);
  const durationSec = useScrollDuration(trackRef, rows);

  if (loop.length === 0) return null;

  return (
    <div className="welcome-scroll-col w-[288px] shrink-0 h-full">
      <div className={offset ? 'welcome-scroll-offset' : undefined}>
        <div
          ref={trackRef}
          className="welcome-scroll-track welcome-scroll-track-up"
          style={{ animationDuration: `${durationSec}s` }}
        >
          {loop.map((row, i) => (
            <WelcomeEditionCard key={`${row.isoDate}-${i}`} row={row} />
          ))}
        </div>
      </div>
    </div>
  );
}

function SkeletonCard() {
  return (
    <div
      className="shrink-0 w-[288px] h-[230px] bg-white border-2 border-[#0A0A0A] rounded animate-pulse"
      aria-hidden="true"
    />
  );
}

export default function WelcomeEditionScroller() {
  const [rows, setRows] = useState(null);
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await api.get('/archive/list?view=editions&limit=14');
        const fetched = data?.rows?.length ? data.rows : PLACEHOLDER_EDITIONS;
        if (!cancelled) {
          setRows(fetched);
          setStatus('ready');
        }
      } catch {
        if (!cancelled) {
          setRows(PLACEHOLDER_EDITIONS);
          setStatus('ready');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const { col0, col1 } = useMemo(() => {
    const source = rows ?? PLACEHOLDER_EDITIONS;
    return splitEditionColumns(source);
  }, [rows]);

  if (status === 'loading') {
    return (
      <div className="welcome-scroller-panel h-full overflow-hidden pl-1 pr-3 sm:pr-4">
        <div className="flex gap-1.5 h-full pb-3 pt-0 justify-start ml-4 sm:ml-5">
          <div className="flex flex-col gap-2 min-h-0">
            <SkeletonCard />
            <SkeletonCard />
          </div>
          <div className="flex flex-col gap-2 min-h-0 welcome-scroll-offset">
            <SkeletonCard />
            <SkeletonCard />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="welcome-scroller-panel h-full overflow-hidden">
      <div className="welcome-scroller-columns flex gap-1.5 h-full pl-1 pr-3 sm:pr-4 pb-3 pt-0 justify-start ml-4 sm:ml-5">
        <WelcomeColumn rows={col0} />
        <WelcomeColumn rows={col1} offset />
      </div>
    </div>
  );
}
